/**
 * Здоровье аккаунта в ячейке статуса (KAN-613): строка "Состояние" и кнопка
 * "Проверить". Только подключённые аккаунты нового держателя.
 * KAN-661: сессия моложе суток не проверяется (запрос @SpamBot отзывал её):
 * кнопка заблокирована с подсказкой, через сколько часов проверка станет доступна.
 * KAN-871: у личного аккаунта менеджера только строка "Состояние", без кнопки.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { checkErrorMessage } from '@/lib/telegramAccountActions';
import { isPersonalTelegramAccount } from '@/lib/telegramPersonalAccount';
import {
  CHECK_BUTTON_LABEL,
  CHECK_BUTTON_PENDING_LABEL,
  healthTitle,
  healthView,
  showsHealth,
} from '@/lib/telegramAccountFormat';
import { errorCode } from '@/lib/telegramProxy';
import {
  FRESH_SESSION_CODE,
  checkAvailableHint,
  freshHoursLeft,
  isFreshSession,
} from '@/lib/telegramSessionAge';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const DOT: Record<string, string> = {
  gray: 'bg-gray-400',
  green: 'bg-green-500',
  yellow: 'bg-yellow-400',
  red: 'bg-red-500',
};

const TEXT: Record<string, string> = {
  gray: 'text-gray-600',
  green: 'text-green-700',
  yellow: 'text-yellow-700',
  red: 'text-red-700',
};

export function AccountHealth({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element | null {
  const queryClient = useQueryClient();
  const key = `${account.operator_id}-${account.account_id}`;

  const check = useMutation({
    mutationFn: () =>
      telegramAccountsApi.checkAccount(account.account_id, account.operator_id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      const view = healthView(result.health);
      toast.success(result.text || view.label);
    },
    onError: (err) => {
      // 409 fresh_session: в таблице аккаунт ещё выглядел старым (гонка с повторным входом),
      // сервер отказал с часами в detail; список обновляем, чтобы кнопка заблокировалась.
      if (errorCode(err) === FRESH_SESSION_CODE) {
        void queryClient.invalidateQueries({ queryKey: ['telegram-accounts'] });
      }
      toast.error(checkErrorMessage(err));
    },
  });

  if (!showsHealth(account)) return null;

  const view = healthView(account.health);
  const title = healthTitle(account.health_text, account.health_at);
  const fresh = isFreshSession(account);
  // KAN-871: личный аккаунт менеджера @SpamBot не проверяется (backend 409).
  const personal = isPersonalTelegramAccount(account);
  const hint = fresh ? checkAvailableHint(freshHoursLeft(account)) : '';

  return (
    <div className="mt-1.5 flex flex-col items-start gap-1">
      <div
        data-testid={`health-${key}`}
        data-health={account.health || ''}
        title={title || undefined}
        className="flex items-center gap-1.5 text-xs"
      >
        <span className="text-gray-500">Состояние:</span>
        <span
          className={`inline-flex items-center gap-1.5 font-medium ${TEXT[view.color]}${
            view.italic ? ' italic font-normal' : ''
          }`}
        >
          <span
            data-testid={`health-dot-${key}`}
            className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${DOT[view.color]}`}
          />
          {view.label}
        </span>
      </div>
      {!personal && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={check.isPending || fresh}
          title={hint || undefined}
          data-testid={`check-${key}`}
          onClick={() => check.mutate()}
        >
          {check.isPending ? CHECK_BUTTON_PENDING_LABEL : CHECK_BUTTON_LABEL}
        </Button>
      )}
      {hint && !personal && (
        <span data-testid={`check-fresh-${key}`} className="text-xs text-gray-500">
          {hint}
        </span>
      )}
    </div>
  );
}
